(() => {
  if (window.RavinScrollDeckLoaded) return;
  window.RavinScrollDeckLoaded = true;

  const instances = new WeakMap();
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  function initDeck(root) {
    if (!root || instances.has(root)) return;
    const stage = root.querySelector('[data-ravin-deck-stage]');
    const cards = Array.from(root.querySelectorAll('[data-ravin-deck-card]'));
    if (!stage || !cards.length) return;

    const current = root.querySelector('[data-ravin-deck-current]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    let frame = 0;
    let lastActive = -1;

    function setCard(card, y, z, tilt, yaw, scale, alpha, layer) {
      const style = card.style;
      style.setProperty('--ravin-deck-y', y.toFixed(2) + 'px');
      style.setProperty('--ravin-deck-z', z.toFixed(2) + 'px');
      style.setProperty('--ravin-deck-tilt', tilt.toFixed(2) + 'deg');
      style.setProperty('--ravin-deck-yaw', yaw.toFixed(2) + 'deg');
      style.setProperty('--ravin-deck-scale', scale.toFixed(4));
      style.setProperty('--ravin-deck-alpha', alpha.toFixed(3));
      style.zIndex = String(layer);
    }

    function render() {
      frame = 0;
      const rect = root.getBoundingClientRect();
      const viewport = window.innerHeight;
      // The Fold reserves a separate scroll runway AFTER the final card.
      // Do not make the regular product cards race through this extra space.
      const foldTail = root.dataset.ravinFoldReady === 'true'
        ? Math.max(0, Number(root.dataset.ravinFoldTailPx) || 0)
        : 0;
      // Frame Transfer overlaps the REAL section behind the preceding
      // Contact Sheet. Hold the genuine first card during that reveal,
      // then continue through the same deck at normal section progress.
      const liveBridgeLead = Math.max(0, Number(root.dataset.ravinBridgeLeadPx) || 0);
      const travel = Math.max(1, rect.height - viewport - foldTail - liveBridgeLead);
      const fraction = clamp((-rect.top - liveBridgeLead) / travel, 0, 1);
      const position = fraction * Math.max(0, cards.length - 1);
      const active = clamp(Math.round(position), 0, cards.length - 1);
      const height = stage.clientHeight;
      const reduced = reduceMotion.matches;

      root.style.setProperty('--ravin-deck-progress', fraction.toFixed(5));
      if (active !== lastActive) {
        if (current) current.textContent = String(active + 1).padStart(2, '0');
        cards.forEach((card, index) => {
          const isActive = index === active;
          card.classList.toggle('is-active', isActive);
          card.toggleAttribute('inert', !isActive);
          if (isActive) card.removeAttribute('aria-hidden');
          else card.setAttribute('aria-hidden', 'true');
        });
        lastActive = active;
      }

      cards.forEach((card, index) => {
        const delta = index - position;
        let y, z, tilt, yaw, scale, alpha;
        if (reduced) {
          y = 0;
          z = 0;
          tilt = 0;
          yaw = 0;
          scale = 1;
          alpha = index === active ? 1 : 0;
        } else if (delta >= 0) {
          const d = Math.min(delta, 5);
          y = -height * (0.11 * d + 0.018 * d * d);
          z = 145 - 190 * d;
          tilt = 73 * Math.min(d, 1) + 3 * Math.max(0, d - 1);
          yaw = Math.sin(index * 1.9) * Math.min(d, 2) * 2.4;
          scale = 1 - Math.min(0.33, 0.075 * d);
          alpha = clamp(1 - Math.max(0, d - 1.3) * 0.32, 0, 1);
        } else {
          const d = -delta;
          y = height * (0.92 * Math.min(d, 1) + 0.5 * Math.max(0, d - 1));
          z = 145 - 245 * Math.min(d, 1) - 80 * Math.max(0, d - 1);
          tilt = -88 * Math.min(d, 1);
          yaw = 0;
          scale = 1 - 0.12 * Math.min(d, 1);
          alpha = clamp(1 - Math.max(0, d - 0.55) / 0.45, 0, 1);
        }
        const layer = 1000 - Math.round(Math.abs(delta) * 100) + (delta < 0 ? 30 : 0);
        setCard(card, y, z, tilt, yaw, scale, alpha, layer);
      });
    }

    function schedule() {
      if (frame) return;
      frame = window.requestAnimationFrame(render);
    }

    window.addEventListener('scroll', schedule, { passive: true, signal: controller.signal });
    window.addEventListener('resize', schedule, { passive: true, signal: controller.signal });
    window.addEventListener('pageshow', schedule, { signal: controller.signal });

    root.addEventListener('shopify:block:select', event => {
      const card = event.target.closest('[data-ravin-deck-card]');
      if (!card || !root.contains(card)) return;
      const index = cards.indexOf(card);
      if (index < 0) return;
      const travel = Math.max(0, root.offsetHeight - window.innerHeight);
      const top = root.getBoundingClientRect().top + window.scrollY;
      const fraction = cards.length > 1 ? index / (cards.length - 1) : 0;
      window.scrollTo({ top: top + travel * fraction, behavior: 'auto' });
      schedule();
    }, { signal: controller.signal });

    if (reduceMotion.addEventListener) {
      reduceMotion.addEventListener('change', schedule, { signal: controller.signal });
    }

    instances.set(root, () => {
      controller.abort();
      if (frame) window.cancelAnimationFrame(frame);
      instances.delete(root);
    });
    render();
  }

  function initIn(scope) {
    if (scope.matches && scope.matches('[data-ravin-3d-deck]')) initDeck(scope);
    if (scope.querySelectorAll) scope.querySelectorAll('[data-ravin-3d-deck]').forEach(initDeck);
  }

  function unloadIn(scope) {
    const roots = [];
    if (scope.matches && scope.matches('[data-ravin-3d-deck]')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-ravin-3d-deck]'));
    roots.forEach(root => {
      const cleanup = instances.get(root);
      if (cleanup) cleanup();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initIn(document), { once: true });
  } else {
    initIn(document);
  }
  document.addEventListener('shopify:section:load', event => initIn(event.target));
  document.addEventListener('shopify:section:unload', event => unloadIn(event.target));
})();
